import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Wallet, CheckCircle2, Clock, Upload, Download, Unlock, ChevronLeft, ChevronRight,
  ExternalLink, AlertCircle, Trash2, ShieldAlert, Euro,
} from 'lucide-react';

import { api } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { useAuth } from '../lib/auth-context';

const MONTH_NAMES = [
  'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
  'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre',
];

function formatEUR(n) {
  return `€ ${Number(n || 0).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function shortDate(iso) {
  if (!iso) return '—';
  // YYYY-MM-DD
  const d = (iso || '').slice(0, 10);
  const [y, m, dd] = d.split('-');
  if (!y || !m || !dd) return d;
  return `${dd}/${m}/${y}`;
}

export default function IncassiPage() {
  const { user } = useAuth() || {};
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [approveOpen, setApproveOpen] = useState(false);
  const [pdfFile, setPdfFile] = useState(null);
  const [approving, setApproving] = useState(false);

  // ★ Storno per garanzia — cancellazione rata (anche su mesi approvati)
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteReason, setDeleteReason] = useState('');
  const [deleting, setDeleting] = useState(false);

  const isAdmin = ['admin', 'amministrativo'].includes(user?.role);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/collections?year=${year}&month=${month}`);
      setData(r.data);
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Errore caricamento incassi';
      toast.error(detail);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => { if (isAdmin) load(); }, [isAdmin, load]);

  const goPrev = () => {
    const m = month - 1;
    if (m < 1) { setMonth(12); setYear(year - 1); } else setMonth(m);
  };
  const goNext = () => {
    const m = month + 1;
    if (m > 12) { setMonth(1); setYear(year + 1); } else setMonth(m);
  };

  const onApprove = async () => {
    if (!pdfFile) { toast.error('Carica il PDF della contabile prima di approvare'); return; }
    setApproving(true);
    try {
      const fd = new FormData();
      fd.append('pdf', pdfFile);
      await api.post(`/collections/${year}/${String(month).padStart(2, '0')}/approve`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`Incasso ${MONTH_NAMES[month - 1]} ${year} approvato`);
      setApproveOpen(false);
      setPdfFile(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore approvazione');
    } finally {
      setApproving(false);
    }
  };

  const onUnlock = async () => {
    if (!window.confirm(`Sblocco il mese ${MONTH_NAMES[month - 1]} ${year}? Le rate torneranno editabili.`)) return;
    try {
      await api.post(`/collections/${year}/${String(month).padStart(2, '0')}/unlock`);
      toast.success('Mese sbloccato');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore sblocco');
    }
  };

  const onDownloadPdf = async () => {
    try {
      const resp = await api.get(`/collections/${year}/${String(month).padStart(2, '0')}/pdf`, {
        responseType: 'blob',
      });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = data?.approval?.pdf_filename || `incassi_${year}_${String(month).padStart(2, '0')}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Errore download PDF');
    }
  };

  // ★ Cancellazione rata — supporta anche mesi approvati con force + motivo storno
  const openDeleteDialog = (installment) => {
    setDeleteTarget(installment);
    setDeleteReason('');
  };

  const closeDeleteDialog = () => {
    if (deleting) return;
    setDeleteTarget(null);
    setDeleteReason('');
  };

  const onConfirmDelete = async () => {
    if (!deleteTarget) return;
    const monthApproved = data?.approval?.status === 'approved';
    if (monthApproved && !(deleteReason || '').trim()) {
      toast.error('Inserisci un motivo dello storno per procedere');
      return;
    }
    setDeleting(true);
    try {
      const params = {};
      if (monthApproved) {
        params.force = 'true';
        params.reason = deleteReason.trim();
      }
      await api.delete(`/installments/${deleteTarget.id}`, { params });
      toast.success(
        monthApproved
          ? 'Rata stornata per garanzia e rimossa dal mese approvato'
          : 'Rata eliminata',
      );
      setDeleteTarget(null);
      setDeleteReason('');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore eliminazione rata');
    } finally {
      setDeleting(false);
    }
  };

  if (!isAdmin) {
    return (
      <div className="p-6 max-w-3xl mx-auto" data-testid="incassi-not-admin">
        <Card className="p-6">
          <div className="flex items-center gap-3 text-amber-700">
            <AlertCircle className="w-6 h-6" />
            <div>
              <div className="font-medium">Sezione riservata all&apos;amministrazione</div>
              <div className="text-sm text-muted-foreground mt-1">
                Solo gli utenti con ruolo Admin o Amministrativo possono accedere a questa pagina.
              </div>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  const approval = data?.approval || {};
  const isApproved = approval?.status === 'approved';
  const installments = data?.installments || [];

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto" data-testid="incassi-page">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="font-serif text-2xl md:text-3xl text-[hsl(var(--primary))] flex items-center gap-2">
            <Wallet className="w-7 h-7" /> Incassi del mese
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Cashflow mensile · Approvazione con PDF della contabile
          </p>
        </div>

        {/* Month/Year picker */}
        <div className="flex items-center gap-2 bg-[var(--surface-1)] rounded-lg border p-1">
          <Button variant="ghost" size="icon" onClick={goPrev} data-testid="incassi-prev-month">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <div className="text-sm font-medium px-3 min-w-[140px] text-center">
            {MONTH_NAMES[month - 1]} {year}
          </div>
          <Button variant="ghost" size="icon" onClick={goNext} data-testid="incassi-next-month">
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-6">
        <Card className="p-4">
          <div className="text-xs uppercase tracking-wide text-muted-foreground">Totale mese</div>
          <div className="text-2xl font-semibold mt-1 tabular-nums" data-testid="kpi-total">
            {formatEUR(data?.total_amount || 0)}
          </div>
        </Card>
        <Card className="p-4">
          <div className="text-xs uppercase tracking-wide text-muted-foreground">Incassato</div>
          <div className="text-2xl font-semibold mt-1 tabular-nums text-emerald-700" data-testid="kpi-paid">
            {formatEUR(data?.paid_amount || 0)}
          </div>
        </Card>
        <Card className="p-4">
          <div className="text-xs uppercase tracking-wide text-muted-foreground">In attesa</div>
          <div className="text-2xl font-semibold mt-1 tabular-nums text-amber-700" data-testid="kpi-pending">
            {formatEUR(data?.pending_amount || 0)}
          </div>
        </Card>
        <Card className="p-4">
          <div className="text-xs uppercase tracking-wide text-muted-foreground"># Rate</div>
          <div className="text-2xl font-semibold mt-1 tabular-nums" data-testid="kpi-count">
            {data?.installments_count || 0}
          </div>
        </Card>
      </div>

      {/* Approval status */}
      <Card className="p-4 mb-6" data-testid="approval-card">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            {isApproved ? (
              <>
                <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <div className="font-medium" data-testid="approval-status">
                    Approvato il {shortDate(approval.approved_at)}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    da {approval.approved_by_name || '—'} · PDF: {approval.pdf_filename || '—'}
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <div className="font-medium" data-testid="approval-status">In attesa di approvazione</div>
                  <div className="text-xs text-muted-foreground">
                    Carica il PDF della contabile e approva per chiudere il mese
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="flex items-center gap-2">
            {isApproved && (
              <>
                <Button variant="outline" size="sm" onClick={onDownloadPdf} data-testid="pdf-download-button">
                  <Download className="w-4 h-4 mr-1" /> Scarica PDF
                </Button>
                <Button variant="outline" size="sm" onClick={onUnlock} data-testid="unlock-button">
                  <Unlock className="w-4 h-4 mr-1" /> Sblocca mese
                </Button>
              </>
            )}
            {!isApproved && (
              <Button onClick={() => setApproveOpen(true)} data-testid="approve-button">
                <Upload className="w-4 h-4 mr-1" /> Approva con PDF
              </Button>
            )}
          </div>
        </div>
      </Card>

      {/* Table */}
      <Card className="overflow-hidden">
        <div className="px-4 py-3 border-b font-medium text-sm bg-[var(--surface-2)]">
          Rate del mese · {MONTH_NAMES[month - 1]} {year}
        </div>
        {loading ? (
          <div className="p-8 text-center text-sm text-muted-foreground">Caricamento…</div>
        ) : installments.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground" data-testid="incassi-empty">
            Nessuna rata in scadenza in questo mese.
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="incassi-table">
            <thead className="text-xs uppercase tracking-wide text-muted-foreground bg-[var(--surface-2)]/50">
              <tr>
                <th className="text-left py-2 px-3">Scadenza</th>
                <th className="text-left py-2 px-3">Lead</th>
                <th className="text-left py-2 px-3">Agente</th>
                <th className="text-right py-2 px-3">Importo</th>
                <th className="text-center py-2 px-3">Stato</th>
                <th className="text-left py-2 px-3">Pagata il</th>
                <th className="py-2 px-3 text-right">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {installments.map((it) => (
                <tr key={it.id} className="border-t hover:bg-[var(--surface-2)]/40" data-testid={`installment-row-${it.id}`}>
                  <td className="py-2 px-3 tabular-nums">{shortDate(it.due_date)}</td>
                  <td className="py-2 px-3">
                    <div className="font-medium">{it.lead_name || '—'}</div>
                    <div className="text-xs text-muted-foreground">{it.lead_email}</div>
                  </td>
                  <td className="py-2 px-3 text-xs">{it.agent_name || '—'}</td>
                  <td className="py-2 px-3 text-right tabular-nums font-medium">{formatEUR(it.amount)}</td>
                  <td className="py-2 px-3 text-center">
                    {it.status === 'paid' ? (
                      <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 hover:bg-emerald-100">Pagata</Badge>
                    ) : (
                      <Badge variant="outline" className="text-amber-700 border-amber-300">In attesa</Badge>
                    )}
                  </td>
                  <td className="py-2 px-3 tabular-nums text-xs">{shortDate(it.paid_date)}</td>
                  <td className="py-2 px-3">
                    <div className="flex items-center justify-end gap-1.5 flex-wrap">
                      <Link
                        to={`/app/leads/${it.lead_id}`}
                        className="text-xs text-[hsl(var(--primary))] hover:underline inline-flex items-center gap-1"
                        data-testid={`installment-open-lead-${it.id}`}
                      >
                        Apri lead <ExternalLink className="w-3 h-3" />
                      </Link>
                      <Button
                        size="sm"
                        variant="destructive"
                        className="h-7 px-2"
                        onClick={() => openDeleteDialog(it)}
                        data-testid={`installment-delete-${it.id}`}
                        aria-label="Cancella / Storno rata"
                        title={isApproved ? 'Storno per garanzia (mese approvato)' : 'Elimina rata'}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {/* Approve dialog */}
      <Dialog open={approveOpen} onOpenChange={setApproveOpen}>
        <DialogContent className="max-w-md" data-testid="approve-dialog">
          <DialogHeader>
            <DialogTitle>Approva incasso · {MONTH_NAMES[month - 1]} {year}</DialogTitle>
            <DialogDescription>
              Carica il PDF cumulativo della contabile con il riepilogo delle rate del mese.
              Una volta approvato, le rate del mese saranno bloccate (solo admin può sbloccare).
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="rounded-md bg-[var(--surface-2)] p-3 text-xs">
              Totale mese: <b>{formatEUR(data?.total_amount || 0)}</b> · {data?.installments_count || 0} rate
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide">PDF contabile *</Label>
              <Input
                type="file"
                accept="application/pdf"
                onChange={(e) => setPdfFile(e.target.files?.[0] || null)}
                data-testid="approve-pdf-input"
                className="mt-1"
              />
              {pdfFile && (
                <div className="text-xs text-muted-foreground mt-1">
                  {pdfFile.name} · {(pdfFile.size / 1024).toFixed(0)} KB
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setApproveOpen(false)} disabled={approving}>
              Annulla
            </Button>
            <Button onClick={onApprove} disabled={!pdfFile || approving} data-testid="approve-confirm">
              {approving ? 'Approvazione…' : 'Conferma approvazione'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete / Storno rata dialog */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => { if (!v) closeDeleteDialog(); }}>
        <AlertDialogContent data-testid="installment-delete-confirm" className="max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-red-600" />
              {isApproved ? 'Storno rata per garanzia' : 'Eliminare questa rata?'}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                {deleteTarget && (
                  <div className="rounded-lg border border-red-200 bg-red-50/50 p-3 space-y-1.5 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-xs uppercase text-muted-foreground">Importo</span>
                      <span className="font-serif text-lg font-semibold text-red-700">
                        <Euro className="w-3.5 h-3.5 inline mr-0.5" />
                        {Number(deleteTarget.amount || 0).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs uppercase text-muted-foreground">Lead</span>
                      <span className="font-medium">{deleteTarget.lead_name || '—'}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs uppercase text-muted-foreground">Scadenza</span>
                      <span>{shortDate(deleteTarget.due_date)}</span>
                    </div>
                    {deleteTarget.paid_date && (
                      <div className="flex items-center justify-between">
                        <span className="text-xs uppercase text-muted-foreground">Pagata il</span>
                        <span>{shortDate(deleteTarget.paid_date)}</span>
                      </div>
                    )}
                    <div className="flex items-center justify-between">
                      <span className="text-xs uppercase text-muted-foreground">Stato</span>
                      <span>{deleteTarget.status === 'paid' ? 'Pagata' : 'In attesa'}</span>
                    </div>
                  </div>
                )}

                {isApproved ? (
                  <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 space-y-2">
                    <div className="flex items-start gap-2">
                      <ShieldAlert className="w-4 h-4 mt-0.5 shrink-0" />
                      <div>
                        <b>Attenzione: mese già approvato.</b>{' '}
                        Stai stornando una rata che risulta già inclusa nel PDF cumulativo
                        del mese <b>{MONTH_NAMES[month - 1]} {year}</b>.
                        L&apos;operazione è irreversibile e verrà registrata nell&apos;audit
                        di questo mese.
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-wide">Motivo storno *</Label>
                      <Textarea
                        value={deleteReason}
                        onChange={(e) => setDeleteReason(e.target.value)}
                        placeholder="Es. rimborso in garanzia entro 14 giorni, cliente ha annullato…"
                        className="mt-1 min-h-[72px] bg-white"
                        data-testid="installment-delete-reason"
                        disabled={deleting}
                      />
                    </div>
                  </div>
                ) : (
                  <div className="text-red-600 font-medium text-sm">
                    L&apos;operazione è irreversibile. La rata verrà rimossa dai KPI, dal
                    piano pagamenti del lead e dalla Dashboard Incassi.
                  </div>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="installment-delete-cancel"
              disabled={deleting}
            >
              Annulla
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={onConfirmDelete}
              disabled={deleting || (isApproved && !deleteReason.trim())}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="installment-delete-confirm-btn"
            >
              <Trash2 className="w-4 h-4 mr-1" />
              {deleting
                ? 'Eliminazione…'
                : isApproved
                  ? 'Sì, storno per garanzia'
                  : 'Sì, elimina rata'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
